import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  X, Heart, Printer, Plus, Trash2, CheckCircle2, ChefHat, Clock,
  Users, Flame, BookOpen, Sparkles, Copy, Edit3, ListPlus, Camera, ImageMinus,
  AlertTriangle, RefreshCw, ScanLine,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { HomeOSAvatar } from "./HomeOSAvatar";
import { RECIPE_CATEGORIES, RECIPE_TAGS, DIFFICULTIES } from "../data/mealsData";
import { RecipePrintCard, RecipePrintStyles, pickRecipeImage } from "./RecipePrintCard";
import { estimateRecipeNutritionLocal } from "../utils/nutritionUtils";
import { IngredientScannerModal } from "./IngredientScannerModal";
import { getHouseholdFoodContext, evaluateMealForHousehold } from "../utils/foodIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   MEAL MODALS — RecipeDetailModal, RecipeEditModal, MealPlanEditModal
   All persist via HomeOSStateProvider → localStorage.
   ════════════════════════════════════════════════════════════════════════ */

function Backdrop({ onClick }) {
  return <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClick} />;
}

function useEscClose(open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

/* ════════════════════════════════════════════════════════════════════════
   RECIPE DETAIL MODAL — beautiful printable recipe page
   ════════════════════════════════════════════════════════════════════════ */

export function RecipeDetailModal({ open, recipe, onClose, onEdit, onAddToPlan }) {
  const { toggleRecipeFavourite, duplicateRecipe, removeRecipe, upsertRecipe, members, kitchenCookingFor } = useHomeOSState();
  useEscClose(open, onClose);

  /* Family suitability — derived from Family Profiles, shown only when meaningful. */
  const household = useMemo(() => {
    if (!open || !recipe) return null;
    const ctx = getHouseholdFoodContext(members, kitchenCookingFor);
    if (!ctx.hasAnyFoodInfo) return null;
    return evaluateMealForHousehold({ recipe }, ctx);
  }, [open, recipe, members, kitchenCookingFor]);

  if (!open || !recipe) return null;

  const handleChangeImage = () => pickRecipeImage((dataUrl) => {
    if (!dataUrl) return;
    upsertRecipe({ ...recipe, imageUrl: dataUrl });
    toast.success("Recipe image updated.");
  });
  const handleRemoveImage = () => {
    upsertRecipe({ ...recipe, imageUrl: "" });
    toast.success("Recipe image removed.");
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center" data-testid="recipe-detail-modal">
      <RecipePrintStyles />
      <Backdrop onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[94vh] overflow-y-auto bg-gray-50 dark:bg-slate-900/50 rounded-t-[28px] sm:rounded-[28px] shadow-none print:max-h-none print:overflow-visible print:shadow-none print:rounded-none print:bg-white print:m-0">
        {/* Top bar — actions OUTSIDE print area */}
        <div className="no-print sticky top-0 z-10 bg-white/95 dark:bg-slate-900/95 backdrop-blur border-b border-gray-100 dark:border-slate-800 px-4 py-2.5 flex items-center gap-1.5 flex-wrap">
          <button onClick={() => onEdit?.(recipe)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200" data-testid="recipe-edit-btn">
            <Edit3 className="w-3.5 h-3.5" strokeWidth={1.5} /> Edit recipe
          </button>
          <button onClick={() => onAddToPlan?.(recipe)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent hover:opacity-80 text-white text-xs font-extrabold" data-testid="recipe-add-to-plan-btn">
            <ListPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add to meal plan
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200" data-testid="recipe-print-btn">
            <Printer className="w-3.5 h-3.5" strokeWidth={1.5} /> Print recipe
          </button>
          <button onClick={() => { duplicateRecipe(recipe.id); toast.success("Recipe duplicated."); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-xs font-extrabold text-gray-700 dark:text-gray-200" data-testid="recipe-duplicate-btn">
            <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> Duplicate
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Delete "${recipe.title}"? This can't be undone.`)) {
                removeRecipe(recipe.id);
                toast.success("Recipe deleted.");
                onClose?.();
              }
            }}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent hover:bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700 text-xs font-extrabold"
            data-testid="recipe-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete
          </button>
          <button onClick={() => toggleRecipeFavourite(recipe.id)} className={`ml-auto w-8 h-8 rounded-full flex items-center justify-center ${recipe.favourite ? "bg-transparent text-white" : "bg-white dark:bg-slate-800 text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700"}`} data-testid="recipe-fav-btn" aria-label="Toggle favourite">
            <Heart className="w-4 h-4" strokeWidth={1.5} fill={recipe.favourite ? "currentColor" : "none"} />
          </button>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-500 flex items-center justify-center" data-testid="recipe-detail-close" aria-label="Close">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        {/* For your household — quiet family context, never printed */}
        {household && household.status !== "none" && (
          <HouseholdSuitability evaluation={household} />
        )}

        {/* Printable card */}
        <div className="px-3 sm:px-6 py-6 print:p-0">
          <RecipePrintCard
            recipe={recipe}
            editable
            onChangeImage={handleChangeImage}
            onRemoveImage={handleRemoveImage}
          />
        </div>
      </div>
    </div>
  );
}

/* ── For your household — calm, human, no medical dashboards ── */
export function HouseholdSuitability({ evaluation }) {
  const serif = { fontFamily: "var(--ara-font-display), 'Times New Roman', serif" };
  const inter = { fontFamily: "Inter, system-ui, sans-serif" };
  const rows = [
    ...evaluation.conflicts.map(c => ({
      key: `c-${c.kind}-${c.term}`,
      who: c.members.join(" · "),
      text: c.kind === "allergy"
        ? `${c.term} listed under allergies`
        : c.kind === "intolerance"
          ? `${c.term} listed as an intolerance`
          : `${c.term} listed as a dietary preference`,
    })),
    ...evaluation.checks.map(c => ({
      key: `k-${c.kind}-${c.term}`,
      who: c.members.join(" · "),
      text: c.kind === "wontEat"
        ? `${c.term} listed under foods they won't eat`
        : c.kind === "sensory"
          ? `${c.term} noted under texture preferences`
          : `${c.term} listed under dislikes`,
    })),
  ];
  const favourites = evaluation.positives;

  return (
    <div className="no-print px-6 sm:px-9 pt-6" data-testid="recipe-household-suitability">
      <p className="text-[11px] tracking-[0.16em] uppercase pb-2" style={{ ...inter, color: "var(--ara-ink-soft)", fontWeight: 500, borderBottom: "1px solid var(--ara-line-soft)" }}>
        For your household
      </p>
      {rows.length === 0 ? (
        <p className="mt-3 italic text-[14px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)" }} data-testid="recipe-household-clear">
          No known conflicts found from the information currently saved in your Family Profiles.
        </p>
      ) : (
        <div className="mt-3 space-y-1.5" data-testid="recipe-household-checks">
          <p className="text-[13px]" style={{ ...inter, color: "var(--ara-ink)", fontWeight: 450 }}>Check before cooking</p>
          {rows.map(r => (
            <p key={r.key} className="text-[13.5px] leading-[1.55]" style={{ ...inter, color: "var(--ara-ink-muted)", fontWeight: 400 }}>
              {r.who} — {r.text}
            </p>
          ))}
        </div>
      )}
      {favourites.length > 0 && (
        <p className="mt-2 italic text-[13.5px] leading-[1.55]" style={{ ...serif, color: "var(--ara-ink-muted)" }} data-testid="recipe-household-favourites">
          {favourites.map(f => `${f.members.join(" and ")} love${f.members.length === 1 ? "s" : ""} ${f.term}`).join(" · ")}.
        </p>
      )}
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl bg-white/80 dark:bg-slate-800/70 border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-3 py-2.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate">{value}</p>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   RECIPE EDIT MODAL — add/edit recipe
   ════════════════════════════════════════════════════════════════════════ */

const EMPTY_RECIPE = {
  id: null, title: "", subtitle: "", imageUrl: "", imageEmoji: "🍽️",
  category: "Dinner", tags: [], prepTime: 10, cookTime: 20, serves: 4,
  difficulty: "Easy", ingredients: [""], instructions: [""], notes: "",
  source: "Family recipe", favourite: false, assignedDefaultCook: null,
  caloriesPerServe: null, macros: { protein: null, carbs: null, fat: null },
  nutritionTotal: { calories: "", protein: "", carbs: "", fat: "" },
  nutritionCalculated: false,
  nutritionEstimateSource: null,   // "local-reference" | "ai-assisted-mock"
  nutritionConfidence: null,       // "Good" | "Medium" | "Low"
  nutritionNotes: "",
  unmatchedIngredients: [],
  estimatedAt: null,
};

const EMOJI_CHOICES = ["🍽️", "🍛", "🍝", "🌮", "🍕", "🥡", "🍔", "🥗", "🥪", "🍜", "🍱", "🍳", "🍞", "🥐", "🍰", "🍪", "🍌", "🥑", "🍅", "🍣"];

export function RecipeEditModal({ open, recipeId, initialDraft, onClose }) {
  const state = useHomeOSState();
  const original = useMemo(() => (state.recipes || []).find(r => r.id === recipeId) || null, [state.recipes, recipeId]);
  const [draft, setDraft] = useState(EMPTY_RECIPE);
  const [aiEstimating, setAiEstimating] = useState(false);
  const [aiEstimate, setAiEstimate] = useState(null); // { totals, perServe, matched, unmatched, confidence, ... }
  const [scannerOpen, setScannerOpen] = useState(false);
  useEscClose(open, onClose);

  useEffect(() => {
    if (!open) return;
    if (original) setDraft({ ...EMPTY_RECIPE, ...original, tags: original.tags || [], ingredients: original.ingredients?.length ? original.ingredients : [""], instructions: original.instructions?.length ? original.instructions : [""] });
    else if (initialDraft) setDraft({ ...EMPTY_RECIPE, ...initialDraft, tags: initialDraft.tags || [], ingredients: initialDraft.ingredients?.length ? initialDraft.ingredients : [""], instructions: initialDraft.instructions?.length ? initialDraft.instructions : [""] });
    else setDraft(EMPTY_RECIPE);
    setAiEstimate(null);
    setAiEstimating(false);
    setScannerOpen(false);
  }, [open, original, initialDraft]);

  if (!open) return null;

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const toggleTag = (t) => setField("tags", draft.tags.includes(t) ? draft.tags.filter(x => x !== t) : [...draft.tags, t]);
  const updateList = (k, idx, val) => setField(k, draft[k].map((x, i) => i === idx ? val : x));
  const addRow = (k) => setField(k, [...draft[k], ""]);
  const removeRow = (k, idx) => setField(k, draft[k].filter((_, i) => i !== idx));

  const onSave = () => {
    if (!draft.title.trim()) { toast.error("Recipe needs a title."); return; }
    const payload = {
      ...draft,
      title: draft.title.trim(),
      ingredients: draft.ingredients.filter(x => String(x).trim()),
      instructions: draft.instructions.filter(x => String(x).trim()),
      prepTime: Number(draft.prepTime) || 0,
      cookTime: Number(draft.cookTime) || 0,
      serves: Number(draft.serves) || 1,
      caloriesPerServe: draft.caloriesPerServe === "" || draft.caloriesPerServe === null ? null : Number(draft.caloriesPerServe),
      macros: {
        protein: draft.macros?.protein === "" || draft.macros?.protein === null || draft.macros?.protein === undefined ? null : Number(draft.macros.protein),
        carbs:   draft.macros?.carbs === ""   || draft.macros?.carbs === null   || draft.macros?.carbs === undefined   ? null : Number(draft.macros.carbs),
        fat:     draft.macros?.fat === ""     || draft.macros?.fat === null     || draft.macros?.fat === undefined     ? null : Number(draft.macros.fat),
      },
      nutritionCalculated: !!draft.nutritionCalculated,
      nutritionEstimateSource: draft.nutritionEstimateSource || null,
      nutritionConfidence: draft.nutritionConfidence || null,
      nutritionNotes: draft.nutritionNotes || "",
      unmatchedIngredients: draft.unmatchedIngredients || [],
      estimatedAt: draft.estimatedAt || null,
    };
    state.upsertRecipe(payload);
    toast.success(original ? "Recipe updated." : "Recipe added to the family book.");
    onClose?.();
  };

  /* Nutrition calculator — divides total by servings. */
  const calcNutrition = () => {
    const servings = Number(draft.serves) || 0;
    if (servings <= 0) { toast.error("Set servings first."); return; }
    const t = draft.nutritionTotal || {};
    const perServe = {
      caloriesPerServe: t.calories === "" || t.calories === undefined ? null : +(Number(t.calories) / servings).toFixed(0),
      protein:          t.protein  === "" || t.protein  === undefined ? null : +(Number(t.protein)  / servings).toFixed(1),
      carbs:            t.carbs    === "" || t.carbs    === undefined ? null : +(Number(t.carbs)    / servings).toFixed(1),
      fat:              t.fat      === "" || t.fat      === undefined ? null : +(Number(t.fat)      / servings).toFixed(1),
    };
    setDraft(d => ({
      ...d,
      caloriesPerServe: perServe.caloriesPerServe,
      macros: { protein: perServe.protein, carbs: perServe.carbs, fat: perServe.fat },
      nutritionCalculated: true,
    }));
    toast.success(`Per-serve nutrition calculated for ${servings} serving${servings === 1 ? "" : "s"}.`);
  };

  /* ── AI-assisted (local mock) nutrition estimator ───────────────────
     Wires into utils/nutritionUtils.js. The estimator is async-shaped so
     a real AI/nutrition-API call can replace it without changing UI code.
     ─────────────────────────────────────────────────────────────────── */
  const runAiNutritionEstimate = async () => {
    const cleanIngredients = (draft.ingredients || []).filter(x => String(x).trim());
    if (cleanIngredients.length === 0) {
      toast("Add ingredients first so ARA can estimate nutrition.");
      return;
    }
    const servings = Number(draft.serves) || 1;
    setAiEstimating(true);
    try {
      // Small artificial delay so the "Calculating…" state is perceivable.
      await new Promise(r => setTimeout(r, 250));
      const result = estimateRecipeNutritionLocal(cleanIngredients, servings);
      setAiEstimate(result);
      setDraft(d => ({
        ...d,
        nutritionTotal: {
          calories: result.totals.calories,
          protein:  result.totals.protein,
          carbs:    result.totals.carbs,
          fat:      result.totals.fat,
        },
        caloriesPerServe: result.perServe.calories,
        macros: {
          protein: result.perServe.protein,
          carbs:   result.perServe.carbs,
          fat:     result.perServe.fat,
        },
        nutritionCalculated: true,
        nutritionEstimateSource: result.source,
        nutritionConfidence: result.confidence,
        nutritionNotes: result.notes,
        unmatchedIngredients: result.unmatched.map(u => u.raw),
        estimatedAt: result.estimatedAt,
      }));
      toast.success(`Nutrition estimated (${result.confidence} confidence).`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("AI nutrition estimate failed:", e);
      toast.error("Couldn't estimate nutrition — try editing the ingredients.");
    } finally {
      setAiEstimating(false);
    }
  };

  const clearAiEstimate = () => {
    setAiEstimate(null);
    setDraft(d => ({
      ...d,
      nutritionEstimateSource: null,
      nutritionConfidence: null,
      nutritionNotes: "",
      unmatchedIngredients: [],
      estimatedAt: null,
    }));
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="recipe-edit-modal">
      <Backdrop onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[94vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home">
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur z-10 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">{original ? "Edit recipe" : "Add recipe"}</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-500 flex items-center justify-center" data-testid="recipe-edit-close"><X className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Title + subtitle */}
          <Field label="Title">
            <input value={draft.title} onChange={e => setField("title", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" placeholder="Butter Chicken" data-testid="recipe-input-title" />
          </Field>
          <Field label="Description (optional)">
            <input value={draft.subtitle} onChange={e => setField("subtitle", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" placeholder="One line about it" data-testid="recipe-input-subtitle" />
          </Field>

          {/* Category + difficulty */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select value={draft.category} onChange={e => setField("category", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-category">
                {RECIPE_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Difficulty">
              <select value={draft.difficulty} onChange={e => setField("difficulty", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-difficulty">
                {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>

          {/* Times + serves */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Prep (min)"><input type="number" min="0" value={draft.prepTime} onChange={e => setField("prepTime", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-prep" /></Field>
            <Field label="Cook (min)"><input type="number" min="0" value={draft.cookTime} onChange={e => setField("cookTime", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-cook" /></Field>
            <Field label="Serves"><input type="number" min="1" value={draft.serves} onChange={e => setField("serves", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-serves" /></Field>
          </div>

          {/* Image — upload + emoji fallback */}
          <Field label="Recipe image">
            <div className="space-y-2" data-testid="recipe-input-image">
              <div className="relative w-full aspect-[4/3] max-h-56 rounded-2xl overflow-hidden     border border-gray-200 dark:border-slate-700">
                {draft.imageUrl ? (
                  <img src={draft.imageUrl} alt={draft.title || "Recipe"} className="absolute inset-0 w-full h-full object-cover" data-testid="recipe-edit-image-preview" />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-6xl" aria-hidden>{draft.imageEmoji || "🍽️"}</span>
                    <p className="mt-1 text-[10px] uppercase tracking-wider text-gray-400">No image yet</p>
                  </div>
                )}
                <div className="absolute bottom-2 right-2 flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => pickRecipeImage((url) => { if (url) setField("imageUrl", url); })}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-gray-200 text-gray-700 text-[10px] font-extrabold shadow-sm"
                    data-testid="recipe-edit-change-image"
                  >
                    <Camera className="w-3 h-3" strokeWidth={3} /> {draft.imageUrl ? "Change image" : "Upload image"}
                  </button>
                  {draft.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setField("imageUrl", "")}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur border border-gray-200 text-[color:var(--ara-ink-muted)] text-[10px] font-extrabold shadow-sm"
                      data-testid="recipe-edit-remove-image"
                    >
                      <ImageMinus className="w-3 h-3" strokeWidth={3} /> Remove
                    </button>
                  )}
                </div>
              </div>
              <details className="rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 px-3 py-2">
                <summary className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 cursor-pointer">Or pick an emoji</summary>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {EMOJI_CHOICES.map(emo => (
                    <button key={emo} type="button" onClick={() => setField("imageEmoji", emo)} className={`w-9 h-9 rounded-xl text-xl flex items-center justify-center border-2 transition ${draft.imageEmoji === emo ? "bg-transparent border-rose-400" : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700"}`}>{emo}</button>
                  ))}
                </div>
              </details>
            </div>
          </Field>

          {/* Tags */}
          <Field label="Tags">
            <div className="flex flex-wrap gap-1.5" data-testid="recipe-input-tags">
              {RECIPE_TAGS.map(t => {
                const active = draft.tags.includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleTag(t)} className={`px-2.5 py-1 rounded-full text-xs font-extrabold border transition ${active ? "bg-transparent text-white border-rose-500" : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700"}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* Ingredients */}
          <Field label="Ingredients">
            <div className="space-y-1.5" data-testid="recipe-input-ingredients">
              {draft.ingredients.map((ing, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input value={ing} onChange={e => updateList("ingredients", idx, e.target.value)} className="flex-1 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" placeholder={`Ingredient ${idx + 1}`} data-testid={`recipe-ingredient-${idx}`} />
                  <button type="button" onClick={() => removeRow("ingredients", idx)} className="w-8 h-8 rounded-lg bg-transparent text-[color:var(--ara-ink-muted)] flex items-center justify-center"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              ))}
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => addRow("ingredients")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-transparent dark:text-[color:var(--ara-ink-muted)] text-xs font-extrabold" data-testid="recipe-add-ingredient">
                  <Plus className="w-3 h-3" strokeWidth={3} /> Add ingredient
                </button>
                <button type="button" onClick={() => setScannerOpen(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-transparent hover:opacity-80 text-white text-xs font-extrabold" data-testid="recipe-scan-barcode-btn">
                  <ScanLine className="w-3 h-3" strokeWidth={3} /> Scan ingredient barcode
                </button>
              </div>
            </div>
          </Field>

          {/* Instructions */}
          <Field label="Instructions">
            <div className="space-y-1.5" data-testid="recipe-input-instructions">
              {draft.instructions.map((step, idx) => (
                <div key={idx} className="flex items-start gap-1.5">
                  <span className="w-7 h-7 rounded-full bg-transparent text-white flex items-center justify-center text-xs font-extrabold flex-shrink-0 mt-1">{idx + 1}</span>
                  <textarea value={step} onChange={e => updateList("instructions", idx, e.target.value)} className="flex-1 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" rows={2} placeholder={`Step ${idx + 1}`} data-testid={`recipe-instruction-${idx}`} />
                  <button type="button" onClick={() => removeRow("instructions", idx)} className="w-8 h-8 rounded-lg bg-transparent text-[color:var(--ara-ink-muted)] flex items-center justify-center mt-1"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              ))}
              <button type="button" onClick={() => addRow("instructions")} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-transparent dark:text-[color:var(--ara-ink-muted)] text-xs font-extrabold" data-testid="recipe-add-instruction">
                <Plus className="w-3 h-3" strokeWidth={3} /> Add step
              </button>
            </div>
          </Field>

          {/* Nutrition calculator — manual / local only */}
          <Field label="Nutrition (per serve)">
            <div className="rounded-2xl bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 p-3 space-y-2.5" data-testid="recipe-nutrition-section">

              {/* AI-assisted estimator action */}
              <div className="rounded-xl border border-[color:var(--ara-line-soft)] dark:border-rose-800 bg-transparent dark:bg-transparent p-2.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
                      <Sparkles className="w-3 h-3" strokeWidth={3} /> ARA nutrition estimator
                    </p>
                    <p className="text-[11px] font-bold text-gray-600 dark:text-gray-300 mt-0.5">Rough estimate from your ingredients — review and edit before saving.</p>
                  </div>
                  <button
                    type="button"
                    onClick={runAiNutritionEstimate}
                    disabled={aiEstimating}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-transparent hover:opacity-80 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-extrabold shadow-sm active:scale-[0.98]"
                    data-testid="recipe-ai-calc-nutrition-btn"
                  >
                    {aiEstimating
                      ? (<><RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> Calculating…</>)
                      : (<><Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Calculate nutrition with ARA</>)}
                  </button>
                </div>
                {aiEstimate && (
                  <AiEstimateCard estimate={aiEstimate} onClear={clearAiEstimate} onRerun={runAiNutritionEstimate} />
                )}
                <p className="mt-2 text-[10px] italic text-gray-500 leading-snug">
                  Rough estimate only — check labels for exact values. Numbers are auto-filled into the editable fields below so you can adjust them freely. Not medical or dietary advice.
                </p>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Per serve · editable</p>
                {aiEstimate && (
                  <p className="text-[9px] italic text-gray-400">Filled from ARA estimate — tweak freely</p>
                )}
              </div>
              <div className="grid grid-cols-4 gap-2">
                <FieldSmall label="Calories">
                  <input type="number" min="0" value={draft.caloriesPerServe ?? ""} onChange={e => setField("caloriesPerServe", e.target.value)} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-cal" />
                </FieldSmall>
                <FieldSmall label="Protein g">
                  <input type="number" min="0" value={draft.macros?.protein ?? ""} onChange={e => setField("macros", { ...(draft.macros || {}), protein: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-protein" />
                </FieldSmall>
                <FieldSmall label="Carbs g">
                  <input type="number" min="0" value={draft.macros?.carbs ?? ""} onChange={e => setField("macros", { ...(draft.macros || {}), carbs: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-carbs" />
                </FieldSmall>
                <FieldSmall label="Fat g">
                  <input type="number" min="0" value={draft.macros?.fat ?? ""} onChange={e => setField("macros", { ...(draft.macros || {}), fat: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-fat" />
                </FieldSmall>
              </div>
              <details className="rounded-lg" data-testid="recipe-nutrition-calculator">
                <summary className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] cursor-pointer select-none">Advanced: calculate from totals</summary>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  <FieldSmall label="Total kcal">
                    <input type="number" min="0" value={draft.nutritionTotal?.calories ?? ""} onChange={e => setField("nutritionTotal", { ...(draft.nutritionTotal || {}), calories: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-total-cal" />
                  </FieldSmall>
                  <FieldSmall label="Total P g">
                    <input type="number" min="0" value={draft.nutritionTotal?.protein ?? ""} onChange={e => setField("nutritionTotal", { ...(draft.nutritionTotal || {}), protein: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-total-protein" />
                  </FieldSmall>
                  <FieldSmall label="Total C g">
                    <input type="number" min="0" value={draft.nutritionTotal?.carbs ?? ""} onChange={e => setField("nutritionTotal", { ...(draft.nutritionTotal || {}), carbs: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-total-carbs" />
                  </FieldSmall>
                  <FieldSmall label="Total F g">
                    <input type="number" min="0" value={draft.nutritionTotal?.fat ?? ""} onChange={e => setField("nutritionTotal", { ...(draft.nutritionTotal || {}), fat: e.target.value })} className="w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5 text-xs font-medium" data-testid="recipe-nut-total-fat" />
                  </FieldSmall>
                </div>
                <button type="button" onClick={calcNutrition} className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-transparent text-white text-[11px] font-extrabold" data-testid="recipe-nut-calc-btn">
                  Calculate per serve
                </button>
                <p className="mt-1 text-[10px] italic text-gray-500">Manual entry — divides totals by servings.</p>
              </details>
            </div>
          </Field>

          {/* Notes + default cook + fav */}
          <Field label="Family notes (optional)">
            <textarea value={draft.notes} onChange={e => setField("notes", e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" placeholder="Any tweaks, tips or memories" data-testid="recipe-input-notes" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Default cook">
              <select value={draft.assignedDefaultCook || ""} onChange={e => setField("assignedDefaultCook", e.target.value || null)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-rose-300" data-testid="recipe-input-cook">
                <option value="">Not assigned</option>
                {(state.members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet").map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                <option value="all">Everyone</option>
              </select>
            </Field>
            <Field label="Family favourite">
              <button type="button" onClick={() => setField("favourite", !draft.favourite)} className={`w-full px-3 py-2.5 rounded-xl border text-sm font-extrabold flex items-center justify-center gap-1.5 ${draft.favourite ? "bg-transparent text-white border-rose-500" : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300"}`} data-testid="recipe-input-favourite" aria-pressed={draft.favourite}>
                <Heart className="w-4 h-4" strokeWidth={1.5} fill={draft.favourite ? "currentColor" : "none"} />{draft.favourite ? "In favourites" : "Add to favourites"}
              </button>
            </Field>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-6 py-3 border-t border-gray-100 dark:border-slate-800 flex items-center gap-2">
          <button onClick={onClose} className="px-3.5 py-2 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="recipe-edit-cancel">Cancel</button>
          <button onClick={onSave} className="ml-auto inline-flex items-center gap-1.5 bg-[color:var(--ara-ink)] hover:opacity-85 text-[color:var(--ara-surface)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md hover:shadow-none active:scale-[0.97]" data-testid="recipe-edit-save">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> {original ? "Save changes" : "Save recipe"}
          </button>
        </div>
      </div>

      <IngredientScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onAddIngredient={(ing) => {
          // Append a human-readable ingredient line + accumulate nutrition into the recipe totals.
          const line = ing.raw || `${ing.quantity}${ing.unit ? ` ${ing.unit}` : ""} ${ing.ingredientName}`.trim();
          setDraft(d => {
            const ingredients = [...(d.ingredients || [])];
            // Replace the first blank slot if present, else push.
            const blankIdx = ingredients.findIndex(x => !String(x || "").trim());
            if (blankIdx >= 0) ingredients[blankIdx] = line;
            else ingredients.push(line);
            const t = d.nutritionTotal || { calories: "", protein: "", carbs: "", fat: "" };
            const num = (v) => (v === "" || v == null) ? 0 : Number(v);
            return {
              ...d,
              ingredients,
              nutritionTotal: {
                calories: +(num(t.calories) + (ing.calories || 0)).toFixed(0),
                protein:  +(num(t.protein)  + (ing.protein  || 0)).toFixed(1),
                carbs:    +(num(t.carbs)    + (ing.carbs    || 0)).toFixed(1),
                fat:      +(num(t.fat)      + (ing.fat      || 0)).toFixed(1),
              },
            };
          });
        }}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function FieldSmall({ label, children }) {
  return (
    <label className="block">
      <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MEAL PLAN EDIT MODAL — edit one day of the weekly plan
   ════════════════════════════════════════════════════════════════════════ */

export function MealPlanEditModal({ open, day, onClose }) {
  const state = useHomeOSState();
  const entry = useMemo(() => {
    const found = (state.weeklyMealPlan || []).find(d => d.day === day);
    if (found) return found;
    /* Fresh households start with an empty plan — edit a virtual day; saving creates it. */
    return day ? { day, dinner: "", dinnerRecipeId: null, breakfast: "", lunch: "", assignedCook: null, status: "Needs decision", notes: "" } : null;
  }, [state.weeklyMealPlan, day]);
  const [draft, setDraft] = useState({});
  const [recipeSearch, setRecipeSearch] = useState("");
  useEscClose(open, onClose);

  useEffect(() => {
    if (!open || !entry) return;
    setDraft({ ...entry });
    setRecipeSearch("");
  }, [open, entry]);

  if (!open || !entry) return null;

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const eligibleRecipes = (state.recipes || []).filter(r => {
    if (!recipeSearch.trim()) return true;
    return r.title.toLowerCase().includes(recipeSearch.toLowerCase());
  });
  const selectedRecipe = (state.recipes || []).find(r => r.id === draft.dinnerRecipeId) || null;

  const onSave = () => {
    state.updateMealPlanDay(entry.day, {
      dinner: draft.dinner || (selectedRecipe ? selectedRecipe.title : ""),
      dinnerRecipeId: draft.dinnerRecipeId || null,
      breakfast: draft.breakfast || "",
      lunch: draft.lunch || "",
      assignedCook: draft.assignedCook || null,
      status: draft.status || "Planned",
      notes: draft.notes || "",
    });
    toast.success(`${entry.day} meal plan saved.`);
    onClose?.();
  };

  const onClear = () => {
    state.clearMealPlanDay(entry.day);
    toast.success(`${entry.day} cleared.`);
    onClose?.();
  };

  const onMarkCooked = () => {
    state.updateMealPlanDay(entry.day, { status: "Cooked" });
    toast.success(`${entry.day}'s dinner marked cooked. Nice work.`);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="meal-plan-edit-modal">
      <Backdrop onClick={onClose} />
      <div className="relative w-full max-w-xl max-h-[94vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-t-[28px] sm:rounded-[28px] shadow-none font-home">
        <div className="sticky top-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur z-10 px-6 pt-5 pb-4 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Plan a day</p>
            <h2 className="text-xl font-extrabold text-gray-900 dark:text-gray-50">{entry.day} dinner</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-500 flex items-center justify-center" data-testid="meal-plan-close"><X className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <Field label="Link a recipe (optional)">
            <input value={recipeSearch} onChange={e => setRecipeSearch(e.target.value)} placeholder="Search the family recipe book…" className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" data-testid="meal-plan-recipe-search" />
            <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50" data-testid="meal-plan-recipe-list">
              {eligibleRecipes.slice(0, 12).map(r => {
                const active = draft.dinnerRecipeId === r.id;
                return (
                  <button key={r.id} type="button" onClick={() => setDraft(d => ({ ...d, dinnerRecipeId: r.id, dinner: r.title }))} className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm border-b border-gray-100 dark:border-slate-700/40 transition ${active ? "bg-transparent dark:bg-transparent" : "hover:bg-white dark:hover:bg-slate-800"}`} data-testid={`meal-plan-recipe-opt-${r.id}`}>
                    <span className="text-xl">{r.imageEmoji || "🍽️"}</span>
                    <span className="flex-1 font-bold text-gray-800 dark:text-gray-100 truncate">{r.title}</span>
                    {active && <CheckCircle2 className="w-4 h-4 text-[color:var(--ara-ink-muted)]" strokeWidth={1.5} />}
                  </button>
                );
              })}
            </div>
            {selectedRecipe && (
              <button type="button" onClick={() => setDraft(d => ({ ...d, dinnerRecipeId: null }))} className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-800 text-xs font-bold text-gray-500" data-testid="meal-plan-clear-recipe">
                <X className="w-3 h-3" strokeWidth={1.5} /> Unlink "{selectedRecipe.title}"
              </button>
            )}
          </Field>

          <Field label="Dinner title">
            <input value={draft.dinner || ""} onChange={e => setField("dinner", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-base font-bold focus:outline-none focus:ring-2 focus:ring-emerald-300" placeholder="What's for dinner?" data-testid="meal-plan-dinner-input" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Assigned cook">
              <select value={draft.assignedCook || ""} onChange={e => setField("assignedCook", e.target.value || null)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-emerald-300" data-testid="meal-plan-cook">
                <option value="">Not assigned</option>
                {(state.members || []).filter(m => m.ageGroup !== "Baby" && m.ageGroup !== "Pet").map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                <option value="all">Everyone</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={draft.status || "Planned"} onChange={e => setField("status", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-emerald-300" data-testid="meal-plan-status">
                {["Planned", "Needs decision", "Cooked", "Leftovers", "Takeaway avoided"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Breakfast (optional)">
              <input value={draft.breakfast || ""} onChange={e => setField("breakfast", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" data-testid="meal-plan-breakfast" />
            </Field>
            <Field label="Lunch (optional)">
              <input value={draft.lunch || ""} onChange={e => setField("lunch", e.target.value)} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" data-testid="meal-plan-lunch" />
            </Field>
          </div>

          <Field label="Notes (optional)">
            <textarea value={draft.notes || ""} onChange={e => setField("notes", e.target.value)} rows={2} className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300" placeholder="Any prefs or reminders for this dinner" data-testid="meal-plan-notes" />
          </Field>
        </div>

        <div className="sticky bottom-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-6 py-3 border-t border-gray-100 dark:border-slate-800 flex flex-wrap items-center gap-2">
          <button onClick={onClear} className="px-3 py-2 rounded-xl text-xs font-bold text-[color:var(--ara-ink-muted)] hover:bg-transparent dark:hover:opacity-80/20" data-testid="meal-plan-clear">Clear day</button>
          <button onClick={onMarkCooked} className="px-3 py-2 rounded-xl text-xs font-bold text-[color:var(--ara-ink-muted)] bg-transparent hover:bg-transparent dark:bg-transparent dark:text-[color:var(--ara-ink-muted)]" data-testid="meal-plan-mark-cooked">Mark cooked</button>
          <button onClick={onClose} className="ml-auto px-3.5 py-2 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-800" data-testid="meal-plan-cancel">Cancel</button>
          <button onClick={onSave} className="inline-flex items-center gap-1.5 bg-transparent hover:opacity-80 text-white font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md hover:shadow-none active:scale-[0.97]" data-testid="meal-plan-save">
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> Save day
          </button>
        </div>
      </div>
    </div>
  );
}

/* helper: get assignee display data */
export function getCookDisplay(state, cookId) {
  if (!cookId) return null;
  if (cookId === "all") return { id: "all", name: "Everyone", isAll: true };
  return (state.members || []).find(m => m.id === cookId) || null;
}

/* ════════════════════════════════════════════════════════════════════════
   AI NUTRITION ESTIMATE CARD — visual result of runAiNutritionEstimate.
   Always labels values as estimates. User can still edit the fields below.
   ════════════════════════════════════════════════════════════════════════ */

function AiEstimateCard({ estimate, onClear, onRerun }) {
  if (!estimate) return null;
  const { totals, perServe, matched, unmatched, confidence } = estimate;
  const tone = confidence === "Good"
    ? "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-emerald-800"
    : confidence === "Medium"
      ? "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-amber-800"
      : "bg-transparent text-[color:var(--ara-ink-muted)] dark:bg-[color:var(--ara-ink)] dark:text-[color:var(--ara-ink-muted)] border-[color:var(--ara-line-soft)] dark:border-rose-800";

  return (
    <div className="mt-2 rounded-xl bg-white dark:bg-slate-800/80 border border-[color:var(--ara-line-soft)] dark:border-violet-800 p-3" data-testid="recipe-ai-estimate-card">
      {/* Prominent "estimate only" banner — first thing the user reads. */}
      <div className="rounded-lg bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-2.5 py-1.5 flex items-start gap-1.5" data-testid="recipe-ai-estimate-disclaimer">
        <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <p className="text-[11px] font-bold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] leading-snug">
          <span className="font-extrabold uppercase tracking-wider">Estimate only.</span> ARA approximated this from a local reference table. Numbers below are rough — please review and edit them before saving. Not medical or dietary advice.
        </p>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
            <Sparkles className="w-3 h-3" strokeWidth={3} /> ARA estimate · approximate values
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border ${tone}`} data-testid="recipe-ai-estimate-confidence" title="How many of your ingredients ARA could match against the reference table.">
          Match quality: {confidence}
        </span>
      </div>

      <div className="mt-2 grid grid-cols-4 gap-1.5 text-[10px] font-bold">
        <Pill label="Est. per serve kcal" value={perServe.calories} testId="recipe-ai-perserve-cal" approx />
        <Pill label="Est. P g"             value={perServe.protein}  testId="recipe-ai-perserve-p"   approx />
        <Pill label="Est. C g"             value={perServe.carbs}    testId="recipe-ai-perserve-c"   approx />
        <Pill label="Est. F g"             value={perServe.fat}      testId="recipe-ai-perserve-f"   approx />
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1.5 text-[10px] font-bold">
        <Pill label="Est. total kcal" value={totals.calories} testId="recipe-ai-total-cal" subtle approx />
        <Pill label="Est. total P"    value={totals.protein}  testId="recipe-ai-total-p"   subtle approx />
        <Pill label="Est. total C"    value={totals.carbs}    testId="recipe-ai-total-c"   subtle approx />
        <Pill label="Est. total F"    value={totals.fat}      testId="recipe-ai-total-f"   subtle approx />
      </div>

      <p className="mt-2 text-[10px] italic text-gray-500 leading-snug">
        ↓ These values are auto-filled into the editable fields below — adjust freely before saving.
      </p>

      <div className="mt-2 text-[11px] font-bold text-gray-700 dark:text-gray-200">
        <p>
          Matched <span className="text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{matched.length}</span> ingredient{matched.length === 1 ? "" : "s"}
          {unmatched.length > 0 && (
            <> · Needs review <span className="text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">{unmatched.length}</span></>
          )}
        </p>
        {unmatched.length > 0 && (
          <div className="mt-1.5 rounded-lg bg-transparent dark:bg-transparent border border-[color:var(--ara-line-soft)] dark:border-amber-800 px-2 py-1.5" data-testid="recipe-ai-unmatched">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" strokeWidth={3} /> Couldn't match — please add these manually
            </p>
            <ul className="mt-0.5 ml-3 list-disc text-[11px] text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">
              {unmatched.slice(0, 6).map((u, i) => (
                <li key={i} className="truncate">{u.raw}</li>
              ))}
              {unmatched.length > 6 && (
                <li className="italic">+{unmatched.length - 6} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={onRerun}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-[11px] font-extrabold text-gray-700 dark:text-gray-200"
          data-testid="recipe-ai-rerun"
        >
          <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Re-estimate
        </button>
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-transparent hover:bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] border border-[color:var(--ara-line-soft)] dark:border-rose-700 text-[11px] font-extrabold"
          data-testid="recipe-ai-clear"
        >
          <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Clear estimate
        </button>
      </div>
    </div>
  );
}

function Pill({ label, value, testId, subtle, approx }) {
  const isMissing = value == null || Number.isNaN(value);
  const display = isMissing ? "—" : (approx ? `~${value}` : value);
  return (
    <div className={`rounded-lg border px-1.5 py-1 ${subtle ? "bg-gray-50 dark:bg-slate-900/40 border-gray-200 dark:border-slate-700" : "bg-transparent dark:bg-transparent border-[color:var(--ara-line-soft)] dark:border-violet-800"}`}>
      <p className="text-[9px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400 leading-none">{label}</p>
      <p className="text-xs font-extrabold text-gray-900 dark:text-gray-50 mt-0.5" data-testid={testId}>{display}</p>
    </div>
  );
}
